"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { toast } from "sonner"
import { useTranslations } from "next-intl"

import { useAuthStore } from "@/stores/auth-store"
import { usePermissions } from "@/hooks/use-permissions"
import { lookupLeadByPhone } from "@/lib/leads-api"
import { onCallCancelled, onCallClaimed, onCallIncoming } from "@/lib/socket"
import {
  acceptWhatsAppCall,
  getActiveWhatsAppCall,
  placeWhatsAppCall,
  rejectWhatsAppCall,
} from "@/lib/whatsapp-calls-api"
import type {
  WhatsAppActiveCall,
  WhatsAppCallIncomingPayload,
} from "@/lib/whatsapp-calls-types"
import { WaxumAudioBridge, WaxumBridgeError } from "@/lib/waxum-audio-bridge"
import { WhatsAppIncomingCallPopup } from "@/components/whatsapp-calls/whatsapp-incoming-call-popup"
import { WhatsAppInCallBar } from "@/components/whatsapp-calls/whatsapp-in-call-bar"
import { WhatsAppResumeCallCard } from "@/components/whatsapp-calls/whatsapp-resume-call-card"
import type { FeatureFlags } from "@/types"

/** idle → ringing (inbound) | dialing (outbound) → in_call → idle */
export type WhatsAppCallState = "idle" | "ringing" | "dialing" | "in_call"

export interface PlaceWhatsAppCallArgs {
  accountId: string
  /** Preferred — the backend resolves the number (works with masked phones). */
  leadId?: string
  /** Only when the viewer can see the number. */
  phone?: string | null
}

interface WhatsAppCallContextValue {
  state: WhatsAppCallState
  incoming: WhatsAppCallIncomingPayload | null
  placeCall: (args: PlaceWhatsAppCallArgs) => Promise<void>
  answer: () => Promise<void>
  decline: () => Promise<void>
  hangup: () => void
  muted: boolean
  setMuted: (muted: boolean) => void
  durationSec: number
  /** True once the far end actually picked up (media is flowing both ways). */
  answered: boolean
}

const WhatsAppCallContext = createContext<WhatsAppCallContextValue | null>(null)

export function useWhatsAppCalls() {
  return useContext(WhatsAppCallContext)
}

const RINGTONE_URL = "/sounds/whatsapp-ring.wav"

/**
 * Flag gate. `whatsapp_calls_enabled` is added to FeatureFlags by the backend
 * wave; read it through a widening cast so this module compiles both before and
 * after that one-liner lands, and require an EXPLICIT `true` — an absent flag
 * (older backend, anonymous page) must render nothing rather than default-allow.
 */
export function useWhatsAppCallsFlag(): boolean {
  const user = useAuthStore((s) => s.user)
  const flags = user?.feature_flags as unknown as
    | (FeatureFlags & { whatsapp_calls_enabled?: boolean })
    | undefined
  return flags?.whatsapp_calls_enabled === true
}

/** Status code of an axios-style rejection, without importing axios types. */
function statusOf(error: unknown): number | undefined {
  return (error as { response?: { status?: number } } | null)?.response?.status
}

export function WhatsAppCallProvider({ children }: { children: React.ReactNode }) {
  const t = useTranslations("whatsappCalls")
  const user = useAuthStore((s) => s.user)
  const token = useAuthStore((s) => s.token)
  const { can } = usePermissions()
  const flagEnabled = useWhatsAppCallsFlag()

  // Only wire anything up when the clinic has the feature AND the user may see
  // calls. Everything below stays inert otherwise (no socket handlers, no UI).
  const enabled = flagEnabled && can("calls.view")

  const [state, setState] = useState<WhatsAppCallState>("idle")
  const [incoming, setIncoming] = useState<WhatsAppCallIncomingPayload | null>(null)
  const [callee, setCallee] = useState<string | null>(null)
  const [muted, setMutedState] = useState(false)
  const [durationSec, setDurationSec] = useState(0)
  const [accepting, setAccepting] = useState(false)
  const [answered, setAnswered] = useState(false)
  // Outbound: the callee's phone has the offer. Real signal from a narrating
  // gateway; with an older one it flips as soon as the media socket is up.
  const [ringing, setRinging] = useState(false)
  // Mic meter lives OUTSIDE the context value on purpose: it updates several
  // times a second and every context consumer (every call button on the page)
  // would re-render with it. Only the in-call bar needs it, and that is a
  // direct child here.
  const [micLevel, setMicLevel] = useState(0)
  const [micSilent, setMicSilent] = useState(false)
  const [resumable, setResumable] = useState<WhatsAppActiveCall | null>(null)
  const [resuming, setResuming] = useState(false)

  const stateRef = useRef<WhatsAppCallState>("idle")
  const incomingRef = useRef<WhatsAppCallIncomingPayload | null>(null)
  const bridgeRef = useRef<WaxumAudioBridge | null>(null)
  const ringtoneRef = useRef<HTMLAudioElement | null>(null)

  useEffect(() => {
    stateRef.current = state
  }, [state])
  useEffect(() => {
    incomingRef.current = incoming
  }, [incoming])

  const reset = useCallback(() => {
    setState("idle")
    setIncoming(null)
    setCallee(null)
    setMutedState(false)
    setDurationSec(0)
    setAccepting(false)
    setAnswered(false)
    setRinging(false)
    setMicLevel(0)
    setMicSilent(false)
    setResuming(false)
  }, [])

  // ---------------------------------------------------------------- ringtone
  const stopRingtone = useCallback(() => {
    const audio = ringtoneRef.current
    if (!audio) return
    try {
      audio.pause()
      audio.currentTime = 0
    } catch {
      // ignore
    }
  }, [])

  useEffect(() => {
    if (state !== "ringing") {
      stopRingtone()
      return
    }
    if (!ringtoneRef.current) {
      const audio = new Audio(RINGTONE_URL)
      audio.loop = true
      audio.volume = 0.55
      ringtoneRef.current = audio
    }
    // Autoplay may be blocked until the user has interacted with the tab —
    // the popup is still shown, we just ring silently.
    ringtoneRef.current.play().catch(() => undefined)
    return () => stopRingtone()
  }, [state, stopRingtone])

  // ------------------------------------------------------------ duration tick
  // Counts billable time, so it starts at the ANSWER, not at call_started —
  // the media socket is up while the far end is still ringing.
  useEffect(() => {
    if (state !== "in_call" || !answered) return
    const timer = setInterval(() => setDurationSec((prev) => prev + 1), 1000)
    return () => clearInterval(timer)
  }, [state, answered])

  // ------------------------------------------------------------- audio bridge
  const teardownBridge = useCallback((reason: string) => {
    const bridge = bridgeRef.current
    bridgeRef.current = null
    bridge?.stop(reason)
  }, [])

  const reportBridgeError = useCallback(
    (error: unknown) => {
      if (error instanceof WaxumBridgeError) {
        if (error.code === "mic_denied") {
          toast.error(t("errors.micDenied"))
          return
        }
        if (error.code === "mic_unavailable" || error.code === "audio_unsupported") {
          toast.error(t("errors.micUnavailable"))
          return
        }
        if (error.code === "no_active_call") {
          // The 30 s re-attach grace expired between the poll and the click.
          toast.info(t("errors.callAlreadyEnded"))
          return
        }
      }
      toast.error(t("errors.connectFailed"))
    },
    [t]
  )

  /** Build + start the bridge. Resolves once `call_started` has been received. */
  const startBridge = useCallback(
    async (args: {
      accountId: string
      mode: "outbound" | "answer" | "attach"
      toJid?: string
      callId?: string
      waxumCallId?: string
      leadId?: string | null
      /** outbound/answer: the grant the REST call returned; attach has none. */
      callGrant?: string | null
    }) => {
      const authToken =
        token ?? (typeof window !== "undefined" ? localStorage.getItem("token") : null)
      if (!authToken) throw new WaxumBridgeError("server_error", "no auth token")

      const bridge = new WaxumAudioBridge({
        mode: args.mode,
        token: authToken,
        accountId: args.accountId,
        toJid: args.toJid,
        callId: args.callId,
        waxumCallId: args.waxumCallId,
        leadId: args.leadId ?? null,
        userId: user?.id ?? null,
        callGrant: args.callGrant ?? null,
        onConnected: (meta) => {
          setState("in_call")
          // A re-attach replays when the call was answered: keep counting
          // from there instead of restarting the clock.
          const answeredAt =
            typeof meta.answered_at === "number" ? meta.answered_at : null
          setDurationSec(
            answeredAt ? Math.max(0, Math.floor((Date.now() - answeredAt) / 1000)) : 0
          )
          // A gateway that narrates the call tells us when the phone rings;
          // an older one cannot, so "media up" is the best we know.
          if (meta.call_states !== true) setRinging(true)
          // A call we just re-attached to is already up; nothing to resume.
          setResumable(null)
        },
        onRinging: () => setRinging(true),
        onAnswered: () => setAnswered(true),
        onMicLevel: (level, silent) => {
          setMicLevel(level)
          setMicSilent(silent)
        },
        onEnded: (reason) => {
          // Remote hangup, proxy teardown or local stop — one path back to idle.
          bridgeRef.current = null
          stopRingtone()
          setResumable(null)
          reset()
          // Why an outbound call ended without a conversation, in words.
          if (reason === "remote_declined") toast.info(t("toast.declined"))
          else if (reason === "remote_busy") toast.info(t("toast.busy"))
          else if (reason === "remote_timeout" || reason === "remote_no_answer") {
            toast.info(t("toast.noAnswer"))
          }
        },
      })
      bridgeRef.current = bridge
      await bridge.start()
    },
    [token, user?.id, reset, stopRingtone, t]
  )

  // --------------------------------------------------------- resume on reload
  // The sidecar keeps a call whose browser vanished alive for 30 s, so a
  // refresh mid-call is recoverable. Ask once on mount; a null answer (or any
  // failure, including a backend that predates the endpoint) shows nothing.
  useEffect(() => {
    if (!enabled || !token) return
    let active = true
    getActiveWhatsAppCall()
      .then((call) => {
        if (active && call && stateRef.current === "idle") setResumable(call)
      })
      .catch(() => undefined)
    return () => {
      active = false
    }
  }, [enabled, token])

  const resumeCall = useCallback(async () => {
    const call = resumable
    if (!call || stateRef.current !== "idle") return
    setResuming(true)
    setState("dialing")
    setCallee(call.lead?.full_name || call.from_number || call.to_number || null)
    try {
      await startBridge({
        accountId: call.account_id,
        mode: "attach",
        waxumCallId: call.waxum_call_id,
        leadId: call.lead?.id ?? null,
      })
      setResumable(null)
    } catch (error) {
      console.error(error)
      reportBridgeError(error)
      teardownBridge("resume_failed")
      // Whatever the reason, that call is not resumable any more.
      setResumable(null)
      reset()
    } finally {
      setResuming(false)
    }
  }, [resumable, startBridge, teardownBridge, reportBridgeError, reset])

  // --------------------------------------------------------------- socket I/O
  useEffect(() => {
    if (!enabled || !token) return

    const cleanupIncoming = onCallIncoming(async (data) => {
      // Single-call policy: one WhatsApp call at a time per agent. A second
      // ring while busy is ignored here; the sidecar's 45 s sweeper marks it
      // missed, which feeds the existing missed-call badge.
      if (stateRef.current !== "idle") {
        console.log("[WhatsAppCalls] Ignoring incoming call while busy:", data.waxum_call_id)
        return
      }

      let enriched = data
      if (!data.lead && data.from_number) {
        try {
          const lead = await lookupLeadByPhone(data.from_number)
          if (lead) {
            enriched = { ...data, lead: { id: lead.id, full_name: lead.full_name } }
          }
        } catch {
          // Lookup failed (masked phone, 404) — ring with the raw number.
        }
      }
      if (stateRef.current !== "idle") return

      setIncoming(enriched)
      setState("ringing")
    })

    const cleanupCancelled = onCallCancelled((data) => {
      if (
        stateRef.current === "ringing" &&
        incomingRef.current?.waxum_call_id === data.waxum_call_id
      ) {
        stopRingtone()
        reset()
      }
    })

    const cleanupClaimed = onCallClaimed((data) => {
      if (
        stateRef.current !== "ringing" ||
        incomingRef.current?.waxum_call_id !== data.waxum_call_id
      ) {
        return
      }
      stopRingtone()
      reset()
      // Only tell the losers — the winner is already in the call UI.
      if (String(data.user_id) !== String(user?.id ?? "")) {
        toast.info(t("toast.answeredByColleague"))
      }
    })

    return () => {
      cleanupIncoming()
      cleanupCancelled()
      cleanupClaimed()
    }
  }, [enabled, token, user?.id, reset, stopRingtone, t])

  // Tear the call down if the provider unmounts (logout, hard navigation).
  useEffect(() => {
    return () => {
      bridgeRef.current?.stop("unmount")
      bridgeRef.current = null
      const audio = ringtoneRef.current
      ringtoneRef.current = null
      try {
        audio?.pause()
      } catch {
        // ignore
      }
    }
  }, [])

  // ----------------------------------------------------------------- actions
  const answer = useCallback(async () => {
    const call = incomingRef.current
    if (!call || stateRef.current !== "ringing") return

    setAccepting(true)
    stopRingtone()

    let callGrant: string | null = null
    try {
      const accepted = await acceptWhatsAppCall(call.phone_call_id)
      callGrant = accepted?.call_grant ?? null
    } catch (error) {
      // 409 = a colleague claimed it first (server-side race resolution).
      if (statusOf(error) === 409) {
        toast.info(t("toast.answeredByColleague"))
      } else {
        console.error(error)
        toast.error(t("errors.answerFailed"))
      }
      reset()
      return
    }

    setCallee(call.lead?.full_name || call.from_number || null)
    setIncoming(null)
    setAccepting(false)

    try {
      await startBridge({
        accountId: call.account_id,
        mode: "answer",
        callId: call.waxum_call_id,
        leadId: call.lead?.id ?? null,
        callGrant,
      })
    } catch (error) {
      console.error(error)
      reportBridgeError(error)
      teardownBridge("answer_failed")
      reset()
    }
  }, [reset, startBridge, stopRingtone, teardownBridge, reportBridgeError, t])

  const decline = useCallback(async () => {
    const call = incomingRef.current
    stopRingtone()
    reset()
    if (!call) return
    try {
      await rejectWhatsAppCall(call.phone_call_id)
    } catch (error) {
      // The call may already be gone (caller hung up) — nothing to surface.
      console.error(error)
    }
  }, [reset, stopRingtone])

  const placeCall = useCallback(
    async (args: PlaceWhatsAppCallArgs) => {
      if (stateRef.current !== "idle") {
        toast.warning(t("toast.callInProgress"))
        return
      }

      setState("dialing")
      setCallee(args.phone ?? null)
      setDurationSec(0)

      let toJid: string
      let leadId: string | null = args.leadId ?? null
      let callGrant: string | null = null
      try {
        const result = await placeWhatsAppCall({
          account_id: args.accountId,
          ...(args.leadId ? { lead_id: args.leadId } : {}),
          ...(args.phone ? { to: args.phone } : {}),
        })
        toJid = result.to_jid
        leadId = result.lead_id ?? leadId
        callGrant = result.call_grant ?? null
      } catch (error) {
        console.error(error)
        toast.error(t("errors.placeCallFailed"))
        reset()
        return
      }

      try {
        await startBridge({
          accountId: args.accountId,
          mode: "outbound",
          toJid,
          leadId,
          callGrant,
        })
      } catch (error) {
        console.error(error)
        reportBridgeError(error)
        teardownBridge("dial_failed")
        reset()
      }
    },
    [reset, startBridge, teardownBridge, reportBridgeError, t]
  )

  const hangup = useCallback(() => {
    stopRingtone()
    if (bridgeRef.current) {
      teardownBridge("local_hangup") // onEnded → reset()
      return
    }
    reset()
  }, [reset, stopRingtone, teardownBridge])

  const setMuted = useCallback((next: boolean) => {
    bridgeRef.current?.setMuted(next)
    setMutedState(next)
  }, [])

  // Memoised so the mic meter (which re-renders this provider several times a
  // second during a call) doesn't re-render every call button in the app.
  const value: WhatsAppCallContextValue = useMemo(
    () => ({
      state,
      incoming,
      placeCall,
      answer,
      decline,
      hangup,
      muted,
      setMuted,
      durationSec,
      answered,
    }),
    [
      state,
      incoming,
      placeCall,
      answer,
      decline,
      hangup,
      muted,
      setMuted,
      durationSec,
      answered,
    ]
  )

  return (
    <WhatsAppCallContext.Provider value={value}>
      {children}

      {enabled && state === "ringing" && incoming && (
        <WhatsAppIncomingCallPopup
          call={incoming}
          onAnswer={answer}
          onDecline={decline}
          busy={accepting}
        />
      )}

      {enabled && (state === "dialing" || state === "in_call") && (
        <WhatsAppInCallBar
          callee={callee}
          dialing={state === "dialing"}
          ringing={ringing}
          answered={answered}
          durationSec={durationSec}
          muted={muted}
          micLevel={micLevel}
          micSilent={micSilent}
          onToggleMute={() => setMuted(!muted)}
          onHangup={hangup}
        />
      )}

      {/* Only while idle and not ringing — it shares the bottom-right slot with
          the call bar, and a live incoming call outranks a stale one. */}
      {enabled && state === "idle" && !incoming && resumable && (
        <WhatsAppResumeCallCard
          call={resumable}
          busy={resuming}
          onResume={resumeCall}
          onDismiss={() => setResumable(null)}
        />
      )}
    </WhatsAppCallContext.Provider>
  )
}
